/**
 * FichaAgente — la ficha de un agente. Se abre desde una tarjeta de «Mi equipo».
 * La puerta real es GET /v1/agentes/{id} (solo admin). Campo vacío = «—»; sin
 * foto o miniatura, iniciales.
 *
 * GEN-AG-002: lectura de la parte en vigor.
 * GEN-AG-003: «Editar» abre un formulario con los 5 textos y los guarda como
 * PROPUESTA (PUT /v1/agentes/{id}/propuesta); vigor no cambia. Con propuesta,
 * la ficha muestra «En vigor» / «Propuesta pendiente» y resalta qué cambia.
 * GEN-AG-004: en edición, «Foto» y «Miniatura» se suben al elegirlas (también
 * como propuesta) y se previsualizan; la tabla compara también las imágenes.
 * GEN-AG-005: «Poner en vigor» (con confirmación) convierte la propuesta en la
 * versión en vigor; el agente habla y se ve según ella desde el siguiente turno.
 * GEN-248a: vista de lectura (foto de cuerpo entero a la izquierda, tarjetas a la
 * derecha) y el campo «Cómo trabaja» (instrucciones).
 * GEN-TL-006: sección «Herramientas» (solo lectura, es lo que dice su ficha de herramientas).
 */

import { useEffect, useState } from 'react'
import {
  MAX_IMAGEN_BYTES, getAgente, getAgenteImagen, cambiarEstadoAgente, guardarPropuesta, ponerEnVigor, subirImagenAgente,
} from '../api/client.js'
import { AgentAvatar } from './AgentAvatar.jsx'
import HerramientasDelAgente from './HerramientasDelAgente.jsx'
import { useRefrescoSesion } from '../context/SessionContext.jsx'

// Imagen en vigor de un agente como blob URL: va por fetch con el token porque
// está tras la puerta de admin del kernel. Sin nombre o si falla -> null.
export function useImagenAgente(agenteId, nombre) {
  const [url, setUrl] = useState(null)

  useEffect(() => {
    setUrl(null)
    if (!nombre) return undefined
    let vivo = true
    let objectUrl = null
    getAgenteImagen(agenteId, nombre)
      .then(blob => {
        if (!vivo) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => {}) // sin imagen: se quedan las iniciales
    return () => {
      vivo = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [agenteId, nombre])

  return url
}

// Motivo de un error de la API para mostrarlo: «código: mensaje (HTTP n)». Sin
// nada de eso (red caída, sesión caducada...), el mensaje del propio error.
export function motivo(err) {
  const partes = [err?.code && err.code !== 'unknown_error' ? err.code : null, err?.message].filter(Boolean)
  const texto = [...new Set(partes)].join(': ')
  return `${texto}${err?.status ? ` (HTTP ${err.status})` : ''}`.trim()
}

const vacio = v => (typeof v === 'string' && v.trim() ? v : '—')

function Imagen({ agenteId, nombre, fichero, etiqueta, size }) {
  const url = useImagenAgente(agenteId, fichero)
  return (
    <figure className="ficha-imagen">
      <AgentAvatar name={nombre} avatarUrl={url} size={size} />
      <figcaption>{etiqueta}</figcaption>
    </figure>
  )
}

// Campos que se muestran, en orden. `texto` da el valor ya legible (vacío = «—»):
// es lo que se compara para resaltar qué cambia entre vigor y propuesta.
const CAMPOS = [
  { id: 'nombre', etiqueta: 'Nombre', texto: v => vacio(v.nombre) },
  { id: 'cargo', etiqueta: 'Cargo', texto: v => vacio(v.cargo) },
  { id: 'perfil', etiqueta: 'Perfil', texto: v => vacio(v.perfil) },
  { id: 'ambito', etiqueta: 'Ámbito', texto: v => vacio(v.ambito) },
  { id: 'instrucciones', etiqueta: 'Cómo trabaja', texto: v => vacio(v.instrucciones) },
  { id: 'caracter', etiqueta: 'Carácter', texto: v => vacio(v.caracter) },
  { id: 'comunicacion', etiqueta: 'Comunicación por defecto', texto: v => vacio(v.comunicacion) },
  { id: 'departamento', etiqueta: 'Departamento', texto: v => vacio(v.departamento?.nombre) },
  { id: 'lidera', etiqueta: 'Lidera el departamento', texto: v => (v.departamento?.lidera ? 'Sí' : 'No') },
  { id: 'estado', etiqueta: 'Estado', texto: v => (v.estado === 'activo' ? 'Activo' : 'Inactivo') },
]

const IMAGENES = [
  { tipo: 'foto', etiqueta: 'Foto' },
  { tipo: 'miniatura', etiqueta: 'Miniatura' },
]

function ImagenCelda({ agenteId, nombre, fichero }) {
  const url = useImagenAgente(agenteId, fichero)
  return <AgentAvatar name={nombre} avatarUrl={url} size={48} />
}

const AVISO_TAMANO = 'La imagen supera 3 MB.'

// GEN-AG-004: un campo de subida con vista previa. Sube al elegir el fichero;
// la vista previa es la imagen que el kernel ya guardó en la propuesta.
function CampoImagen({ agenteId, nombre, tipo, etiqueta, fichero, onSubida }) {
  const url = useImagenAgente(agenteId, fichero)
  const [subiendo, setSubiendo] = useState(false)
  const [error, setError] = useState(null)

  const elegir = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    // Validación previa: no se envía lo que el kernel va a rechazar con 413.
    if (file.size > MAX_IMAGEN_BYTES) {
      setError(AVISO_TAMANO)
      return
    }
    setSubiendo(true)
    setError(null)
    try {
      onSubida(await subirImagenAgente(agenteId, tipo, file))
    } catch (err) {
      setError(
        err?.status === 413 ? AVISO_TAMANO
          : err?.status === 415 ? 'Solo se admiten imágenes JPEG o PNG.'
            : 'No se ha podido subir la imagen.',
      )
    } finally {
      setSubiendo(false)
    }
  }

  return (
    <div className="ficha-form__imagen">
      <AgentAvatar name={nombre} avatarUrl={url} size={tipo === 'foto' ? 96 : 48} />
      <label>{etiqueta}
        <input type="file" accept="image/jpeg,image/png" onChange={elegir} disabled={subiendo} />
      </label>
      {subiendo && <span className="equipo-estado">Subiendo…</span>}
      {error && <span className="equipo-estado" role="alert">{error}</span>}
    </div>
  )
}

// GEN-248a: vista de lectura de la parte en vigor. Foto de cuerpo entero a la
// izquierda (sin recortar), tarjetas a la derecha; en pantalla estrecha, la foto
// arriba y las tarjetas debajo.
function TarjetaTexto({ titulo, texto }) {
  return (
    <section className="ficha-card">
      <h3 className="ficha-card__titulo">{titulo}</h3>
      <p className="ficha-card__texto">{texto}</p>
    </section>
  )
}

function LecturaFicha({ agenteId, vigor }) {
  const foto = useImagenAgente(agenteId, vigor.foto)
  const miniatura = useImagenAgente(agenteId, vigor.miniatura)
  const activo = vigor.estado === 'activo'
  return (
    <div className="ficha-lectura">
      <div className="ficha-lectura__foto">
        {foto
          ? <img src={foto} alt={`Foto de ${vigor.nombre}`} />
          : <AgentAvatar name={vigor.nombre} avatarUrl={null} size={160} />}
      </div>
      <div className="ficha-lectura__tarjetas">
        <section className="ficha-card ficha-card--cabecera">
          <h2>{vacio(vigor.nombre)}</h2>
          <span className="equipo-card__depto">{vacio(vigor.departamento?.nombre)}</span>
          {vigor.cargo?.trim() && <span className="ficha-card__cargo">{vigor.cargo}</span>}
          <span className="equipo-card__chips">
            {vigor.departamento?.lidera && <span className="equipo-chip equipo-chip--lidera">Lidera</span>}
            <span className={`equipo-chip equipo-chip--${activo ? 'activo' : 'inactivo'}`}>
              {activo ? 'Activo' : 'Inactivo'}
            </span>
          </span>
        </section>
        <section className="ficha-card ficha-card--caracter">
          <div>
            <h3 className="ficha-card__titulo">Carácter</h3>
            <p className="ficha-card__texto">{vacio(vigor.caracter)}</p>
          </div>
          <AgentAvatar name={vigor.nombre} avatarUrl={miniatura} size={56} />
        </section>
        <TarjetaTexto titulo="Perfil" texto={vacio(vigor.perfil)} />
        <TarjetaTexto titulo="Ámbito" texto={vacio(vigor.ambito)} />
        <TarjetaTexto titulo="Cómo trabaja" texto={vacio(vigor.instrucciones)} />
        <TarjetaTexto titulo="Comunicación por defecto" texto={vacio(vigor.comunicacion)} />
        <HerramientasDelAgente agenteId={agenteId} />
      </div>
    </div>
  )
}

// GEN-AG-003: «En vigor» / «Propuesta pendiente», con las filas que cambian resaltadas.
function Comparacion({ agenteId, vigor, propuesta }) {
  return (
    <table className="ficha-comparacion">
      <thead>
        <tr><th scope="col">Campo</th><th scope="col">En vigor</th><th scope="col">Propuesta pendiente</th></tr>
      </thead>
      <tbody>
        {IMAGENES.map(({ tipo, etiqueta }) => {
          const cambia = (vigor[tipo] ?? null) !== (propuesta[tipo] ?? null)
          return (
            <tr key={tipo} className={cambia ? 'ficha-comparacion__cambia' : undefined} data-cambia={cambia || undefined}>
              <th scope="row">{etiqueta}{cambia && <span className="ficha-cambia"> · cambia</span>}</th>
              <td><ImagenCelda agenteId={agenteId} nombre={vigor.nombre} fichero={vigor[tipo]} /></td>
              <td><ImagenCelda agenteId={agenteId} nombre={propuesta.nombre} fichero={propuesta[tipo]} /></td>
            </tr>
          )
        })}
        {CAMPOS.map(c => {
          const cambia = c.texto(vigor) !== c.texto(propuesta)
          return (
            <tr key={c.id} className={cambia ? 'ficha-comparacion__cambia' : undefined} data-cambia={cambia || undefined}>
              <th scope="row">{c.etiqueta}{cambia && <span className="ficha-cambia"> · cambia</span>}</th>
              <td>{c.texto(vigor)}</td>
              <td>{c.texto(propuesta)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// GEN-AG-003: los 5 campos editables. Se precarga con la propuesta si la hay,
// si no con vigor. Nombre, estado, foto y miniatura no se editan aquí.
function FormularioPropuesta({ agenteId, base, imagenes, onImagenSubida, onGuardada, onCancelar }) {
  const [form, setForm] = useState({
    cargo: base.cargo ?? '',
    perfil: base.perfil ?? '',
    ambito: base.ambito ?? '',
    instrucciones: base.instrucciones ?? '',
    caracter: base.caracter ?? '',
    comunicacion: base.comunicacion ?? '',
    departamento: { nombre: base.departamento?.nombre ?? '', lidera: !!base.departamento?.lidera },
  })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  const campo = id => ({ value: form[id], onChange: e => setForm({ ...form, [id]: e.target.value }) })
  const depto = (k, valor) => setForm({ ...form, departamento: { ...form.departamento, [k]: valor } })

  const guardar = async e => {
    e.preventDefault()
    setGuardando(true)
    setError(null)
    try {
      onGuardada(await guardarPropuesta(agenteId, form))
    } catch (err) {
      setError(`No se ha podido guardar la propuesta. ${motivo(err)}`)
      setGuardando(false)
    }
  }

  return (
    <form className="ficha-form" onSubmit={guardar}>
      <div className="ficha-form__imagenes">
        {IMAGENES.map(({ tipo, etiqueta }) => (
          <CampoImagen key={tipo} agenteId={agenteId} nombre={imagenes.nombre} tipo={tipo}
            etiqueta={etiqueta} fichero={imagenes[tipo]} onSubida={onImagenSubida} />
        ))}
      </div>
      <label>Cargo<input type="text" {...campo('cargo')} /></label>
      <label>Perfil<textarea rows={3} {...campo('perfil')} /></label>
      <label>Ámbito<textarea rows={3} {...campo('ambito')} /></label>
      <label>Cómo trabaja<textarea rows={3} {...campo('instrucciones')} /></label>
      <label>Carácter<textarea rows={3} {...campo('caracter')} /></label>
      <label>Comunicación por defecto<textarea rows={3} {...campo('comunicacion')} /></label>
      <label>Departamento
        <input type="text" value={form.departamento.nombre} onChange={e => depto('nombre', e.target.value)} />
      </label>
      <label className="ficha-form__casilla">
        <input type="checkbox" checked={form.departamento.lidera} onChange={e => depto('lidera', e.target.checked)} />
        Lidera el departamento
      </label>
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      <div className="ficha-form__acciones">
        <button type="submit" className="btn btn--primary" disabled={guardando}>Guardar propuesta</button>
        <button type="button" className="btn btn--ghost" onClick={onCancelar} disabled={guardando}>Cancelar</button>
      </div>
    </form>
  )
}

// GEN-AG-005: confirmación simple en línea antes de poner en vigor.
function PonerEnVigor({ agenteId, onPuesta }) {
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)

  const confirmar = async () => {
    setEnviando(true)
    setError(null)
    try {
      onPuesta(await ponerEnVigor(agenteId))
    } catch {
      setError('No se ha podido poner en vigor la propuesta.')
      setEnviando(false)
    }
  }

  if (!confirmando) {
    return (
      <button type="button" className="btn btn--primary" onClick={() => setConfirmando(true)}>
        Poner en vigor
      </button>
    )
  }
  return (
    <div className="ficha-confirmar" role="group" aria-label="Confirmar puesta en vigor">
      <span>¿Poner en vigor la propuesta? El agente hablará y se verá según ella desde el siguiente turno.</span>
      <button type="button" className="btn btn--primary" onClick={confirmar} disabled={enviando}>
        Sí, poner en vigor
      </button>
      <button type="button" className="btn btn--ghost" onClick={() => setConfirmando(false)} disabled={enviando}>
        Cancelar
      </button>
      {error && <span className="equipo-estado" role="alert">{error}</span>}
    </div>
  )
}

// GEN-AG-008: activar o desactivar. Directo, sin confirmación ni propuesta.
function CambiarEstado({ agenteId, estado, onCambiado }) {
  const refrescarSesion = useRefrescoSesion()
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const activo = estado === 'activo'
  async function cambiar() {
    setEnviando(true)
    setError(null)
    try {
      onCambiado(await cambiarEstadoAgente(agenteId, activo ? 'inactivo' : 'activo'))
      // GEN-PT-002: la barra lateral y el inicio salen del descriptor; se
      // re-bootstrapea para que reflejen el cambio sin recargar. Si falla, el
      // cambio ya está hecho: no se muestra como error del cambio.
      refrescarSesion?.().catch(() => {})
    } catch {
      setError('No se ha podido cambiar el estado.')
    } finally {
      setEnviando(false)
    }
  }
  return (
    <>
      <button type="button" className="btn btn--ghost" disabled={enviando} onClick={cambiar}>
        {activo ? 'Desactivar' : 'Activar'}
      </button>
      {error && <span className="equipo-estado" role="alert">{error}</span>}
    </>
  )
}

export default function FichaAgente({ agenteId, onVolver }) {
  const [datos, setDatos] = useState(null)
  const [editando, setEditando] = useState(false)
  const [puesta, setPuesta] = useState(false)

  useEffect(() => {
    let vivo = true
    getAgente(agenteId)
      .then(d => vivo && setDatos(d))
      .catch(() => vivo && setDatos({ error: 'No se ha podido cargar la ficha.' }))
    return () => { vivo = false }
  }, [agenteId])

  const v = datos?.vigor
  const p = datos?.propuesta
  return (
    <section className="mec-visor">
      <button type="button" className="ficha-volver" onClick={onVolver}>← Mi equipo</button>
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {v && (
        <>
          {editando ? (
            <>
              <h2>{vacio(v.nombre)}</h2>
              <FormularioPropuesta
              agenteId={datos.id}
              base={p ?? v}
              imagenes={p ?? v}
              onImagenSubida={({ fichero, ...d }) => setDatos(d)}
              onGuardada={d => { setDatos(d); setEditando(false) }}
              onCancelar={() => setEditando(false)}
              />
            </>
          ) : (
            <>
              {p && <p className="ficha-aviso" role="status">Hay una propuesta pendiente</p>}
              {puesta && !p && (
                <p className="ficha-aviso ficha-aviso--ok" role="status">
                  Propuesta puesta en vigor. Se aplica desde el siguiente turno.
                </p>
              )}
              <LecturaFicha agenteId={datos.id} vigor={v} />
              {p && <Comparacion agenteId={datos.id} vigor={v} propuesta={p} />}
              <div className="ficha-form__acciones">
                <button type="button" className="btn btn--ghost" onClick={() => setEditando(true)}>Editar</button>
                <CambiarEstado agenteId={datos.id} estado={v.estado} onCambiado={setDatos} />
                {p && <PonerEnVigor agenteId={datos.id} onPuesta={d => { setDatos(d); setPuesta(true) }} />}
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}
